import classNames from 'classnames';
import dayjs from 'dayjs';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { CSSProperties, MouseEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceCallback } from 'usehooks-ts';
import { useTeachingBlockCardMenu } from '../../../hooks/useTeachingBlockCardMenu';
import { getCardTextColor } from '../../../utils/getCardTextColor';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { formatCardTitleWithDeviation } from '../cardLengthDeviation';
import { cardMatchesSearch } from '../cardSearch';
import { getTeachingBlockCardGeometry } from '../getTeachingBlockCardGeometry';
import { useTeachingBlockCardResize } from '../hooks/useTeachingBlockCardResize';
import { TeachingBlockGridCardView } from '../TeachingBlockGridCardView/TeachingBlockGridCardView';
import { DOUBLE_CLICK_DELAY_MS, Item, isCoexistableTeachingBlockCard, TeachingBlockVirtualCard } from '../types';
import styles from './VirtualGridCard.module.scss';

type VirtualGridCardProps = {
  card: TeachingBlockVirtualCard;
  row: Item;
};

export const VirtualGridCard = observer(({ card, row }: VirtualGridCardProps) => {
  const store = useTeachingBlockStore();
  const { t } = useTranslation();

  const parent = store.getParentCard(card.parentUuid);
  if (!parent) throw new Error('parent is undefined');

  // Perf: KEIN globales `store.pickedCard`-Abo. Die gepickte Karte ist aus dem Grid
  // gefiltert (virtualCardsInWeek) und hier nie gerendert – das Abo würde nur einen
  // Full-Grid-Rerender beim Aufnehmen auslösen.
  const isPicked = false;
  const isPinned = parent.uuid === store.pinnedCard?.uuid;
  const isLocked = parent.locked;

  const hasWarnings = store.getWarningsForCard(parent.uuid).length > 0;
  const hasAvailabilityWarnings = !!parent.availabilityWarnings && parent.availabilityWarnings.length > 0;
  const showWarnings = store.highlightMode.includes('warnings') && (hasWarnings || hasAvailabilityWarnings);

  // Längen-Abweichung (resizte Epoche) der Elternkarte; nur bei aktivem Toggle.
  const lengthDeviationDays = store.getCardLengthDeviationDays(parent);
  const lengthDeviationActive = store.highlightMode.includes('lengthDeviation');
  const showLengthDeviation = lengthDeviationActive && lengthDeviationDays !== 0;
  const dimmedByLengthDeviation = lengthDeviationActive && lengthDeviationDays === 0;
  const virtualSiblings = store.virtualCards.filter((virtualCard) => virtualCard.parentUuid === parent.uuid);
  const firstVirtualCard = virtualSiblings[0];
  const lastVirtualCard = virtualSiblings[virtualSiblings.length - 1];
  const canResizeStart = firstVirtualCard?.uuid === card.uuid;
  const canResizeEnd = lastVirtualCard?.uuid === card.uuid;

  // Kartensuche: nicht passende platzierte Karten ausgrauen (reaktiv via observer).
  const dimmedBySearch = store.cardSearchString.trim() !== '' && !cardMatchesSearch(parent, store.cardSearchString);
  const dimmed = dimmedBySearch || dimmedByLengthDeviation;

  const borders = card.duration > 7 ? Math.floor((card.duration / 7) * 2) - 1 : 1;
  let offset = dayjs(card.startDateString).day() - 1;
  if (offset < 0) offset = 6;

  const gridCard =
    store.gridContext === 'classes'
      ? parent.classGridCards.find((c) => c.classUuid === row?.value)
      : store.gridContext === 'teachers'
        ? parent.teacherGridCards.find((t) => t.teacherUuid === row?.value)
        : store.gridContext === 'rooms'
          ? parent.roomGridCards.find((r) => r.roomUuid === row?.value)
          : null;

  // Farbe (Fach-Gradient) ist über alle Grid-Card-Typen identisch. Bei frisch zugewiesenem
  // Raum fehlt der roomGridCard noch → Fallback auf eine Klassen-/Lehrer-Card, damit die
  // (Segment-)Karte nicht grau erscheint.
  const colorCard = gridCard?.cssGradientString
    ? gridCard
    : (parent.classGridCards[0] ?? parent.teacherGridCards[0] ?? parent.roomGridCards[0]);

  // Darstellung „Fachfarbe für Kärtchen": gilt für alle Segmente der Elternkarte gemeinsam,
  // sonst würde ein Ferien-Segment aus der Farbgebung herausfallen.
  const subjectColor = store.cardColorBySubject ? (parent.subject?.timetableConfig?.color ?? null) : null;

  function onCardEditOpen() {
    store.setCardDurationModal({
      isOpen: true,
      card: parent ?? undefined,
      isVirtual: true,
    });
  }

  const { menu, hasRooms } = useTeachingBlockCardMenu({
    card: parent,
    styles,
    hasWarnings,
    hasAvailabilityWarnings,
    isPicked,
    isPinned,
    onCardEditOpen,
  });

  const range = useMemo(
    () => ({
      startDateString: card.startDateString,
      endDateString: card.endDateString,
    }),
    [card.startDateString, card.endDateString],
  );
  const cardRef = useRef<HTMLDivElement>(null);

  const { activeEdge, endHandleProps, startHandleProps } = useTeachingBlockCardResize({
    card: parent,
    cardRef,
    getPreviewGeometry: (previewRange) =>
      getTeachingBlockCardGeometry(previewRange, {
        anchorDateString: range.startDateString,
        borderMode: 'virtual',
      }),
    isVirtual: true,
    range,
    canResizeStart,
    canResizeEnd,
  });

  const menuClasses = classNames(styles.menu, { [styles['has-warning']]: showWarnings });

  // s. GridCard: Marker für die Pointer-Durchlässigkeit während eines Wahlfach-Picks.
  const cardClasses = classNames(
    styles['virtual-grid-card'],
    isCoexistableTeachingBlockCard(parent) && 'coexistable-card',
    {
      [styles.picked]: isPicked,
      [styles.pinned]: isPinned,
      [styles.locked]: isLocked,
      [styles['is-resizing']]: !!activeEdge,
      [styles['resizing-start']]: activeEdge === 'start',
      [styles['resizing-end']]: activeEdge === 'end',
      [styles.warning]: showWarnings,
      [styles.room]: store.highlightMode.includes('rooms') && !hasRooms,
      // [styles['high-light']]:
      //   pinboardStore.controlMode.isActive &&
      //   pinboardStore.controlMode.highlights.length !== 0 &&
      //   pinboardStore.controlMode.highlights.includes(card.uuid),
      // [styles['low-light']]:
      //   pinboardStore.controlMode.isActive &&
      //   pinboardStore.controlMode.highlights.length !== 0 &&
      //   !pinboardStore.controlMode.highlights.includes(card.uuid),
    },
  );

  const debounceOnCardHover = useDebounceCallback(() => {
    store.selectCard(toJS(parent));
  }, 500);

  const onMouseEnter = useCallback(() => {
    if (store.resizingCard) return;
    if (!store.selectedCard) {
      store.selectCard(toJS(parent));
    } else {
      debounceOnCardHover();
    }
  }, [parent, debounceOnCardHover, store]);

  const onMouseLeave = useCallback(() => {
    if (store.resizingCard) return;
    debounceOnCardHover.cancel();
    // Perf: `store` (stabil) als Dependency statt `store.resizingCard` – der Callback
    // liest den Wert live; ein Render-Abo würde sonst jede Karte bei Resize neu rendern.
  }, [debounceOnCardHover, store]);

  const onOpenChange = useCallback(() => {
    debounceOnCardHover.cancel();
    store.selectCard(toJS(parent));
  }, [parent, debounceOnCardHover, store.selectCard]);

  // Single- vs. Doppelklick entkoppeln: Der Einzelklick (Karte aufnehmen) wird kurz
  // verzögert, damit ein Doppelklick ihn abbrechen kann (sonst nimmt der erste Klick
  // die Karte bereits auf und der Doppelklick greift nie).
  const clickTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (clickTimeout.current) clearTimeout(clickTimeout.current);
    },
    [],
  );

  const onCardClickDeferred = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const position = { x: event.pageX, y: event.pageY };
      // Shift-Zustand vor dem Timeout festhalten (das Event ist danach nicht mehr gültig).
      const isCopy = event.shiftKey;
      if (clickTimeout.current) clearTimeout(clickTimeout.current);
      clickTimeout.current = setTimeout(() => {
        clickTimeout.current = null;
        // Raum-Kontext: Quell-Raum (die Zeile) merken; Shift → Kopiermodus (mehrere Räume).
        store.pickCard(parent, position, false, store.gridContext === 'rooms' ? row.value : undefined, isCopy);
      }, DOUBLE_CLICK_DELAY_MS);
    },
    [parent, store, row.value],
  );

  const onCardDoubleClick = useCallback(() => {
    if (clickTimeout.current) {
      clearTimeout(clickTimeout.current);
      clickTimeout.current = null;
    }
    // Falls der verzögerte Einzelklick die Karte bereits aufgenommen hat, den Pick
    // sauber zurücknehmen (stellt die Verfügbarkeit wieder her), bevor Auto-Fit läuft.
    if (store.pickedCard?.card.uuid === parent.uuid) store.onEscape(parent);
    // Doppelklick passt das angeklickte Segment strikt in seine Lücke ein – beide
    // Kanten dürfen sich bewegen (die Segment-Beschränkung gilt nur fürs Drag-Resize).
    void store.autoFitCardToGap(parent, {
      isVirtual: true,
      range,
    });
  }, [parent, range, store]);

  // Das Stapel-Layout hängt an der Elternkarte (nur die ist platziert), gilt aber für
  // alle ihre Ferien-Segmente – sonst würde ein Segment aus dem Stapel herausspringen.
  const coexist = store.getCoexistLayout(row.value, parent.uuid);

  const cardStyle = {
    '--card-left-base': `${(offset / 7) * 100}%`,
    '--card-width-base': `calc(${(card.duration / 7) * 100}% + ${borders}px)`,
    background: subjectColor ?? colorCard?.cssGradientString ?? 'var(--color-grey)',
    color: subjectColor ? getCardTextColor(subjectColor) : (colorCard?.textColor ?? 'var(--grid-card-text)'),
    filter: dimmed ? 'grayscale(1)' : undefined,
    height: coexist ? `${100 / coexist.divisions}%` : (gridCard?.cssHeight ?? '100%'),
    opacity: dimmed ? 0.2 : undefined,
    top: coexist ? `${(coexist.track * 100) / coexist.divisions}%` : (gridCard?.cssTop ?? '0%'),
  } as CSSProperties;

  // Darstellung: LuL-Kürzel statt Fach-Kürzel auf dem Kärtchen (nichts anzeigen, wenn keine Lehrkraft).
  const teacherShortNames = parent.teachers
    .map((teacher) => teacher.shortName)
    .filter(Boolean)
    .join(', ');
  const labelShort = store.showTeacherInsteadOfSubject ? teacherShortNames : parent.labelShort;

  return (
    <TeachingBlockGridCardView
      activeEdge={activeEdge}
      canResizeEnd={canResizeEnd}
      canResizeStart={canResizeStart}
      cardClassName={cardClasses}
      cardRef={cardRef}
      cardStyle={cardStyle}
      endHandleProps={endHandleProps}
      hasRooms={store.highlightMode.includes('noIcons') ? true : hasRooms}
      isLocked={!!isLocked}
      isPinned={!!isPinned}
      labelShort={labelShort}
      menu={menu}
      menuClassName={menuClasses}
      onCardClick={onCardClickDeferred}
      onCardDoubleClick={onCardDoubleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onOpenChange={onOpenChange}
      parent={document.getElementById('teaching-block-grid')}
      startHandleProps={startHandleProps}
      styles={styles}
      title={showLengthDeviation ? formatCardTitleWithDeviation(parent.label, lengthDeviationDays, t) : parent.label}
    />
  );
});
